/* eslint-disable testing-library/no-debugging-utils -- intentional deb logs */
import { type FC, useEffect, useState, useMemo, useRef, useContext, createContext, type PropsWithChildren } from 'react'
import { LDProvider, useLDClient, useFlags as useLDFlags } from 'launchdarkly-react-client-sdk'
import { produce } from 'immer'
import { isValidConfig } from '@egw/config'
import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { platformService } from '@egw/platform-service'
import { type Config, configSchema } from '../../config'
import { type MultiContext, type DeviceData } from '../../models'

const log = new Logger('Feature Flags Service - LaunchDarkly')

type Flags = Record<string, unknown>

/**
 * The Feature Flags state property been injected to a component using withUser HOC or provided by useFlags hook
 */
export interface FeatureFlagsState {
  flags: Flags
  isLoading: boolean
}

const initialState: FeatureFlagsState = {
  flags: {},
  isLoading: true,
}

export const FeatureFlagsContext = createContext(initialState)

interface IdentifyUserComponentProps extends PropsWithChildren {
  defaultFlags?: Flags
}

const IdentifyUserComponent: FC<IdentifyUserComponentProps> = ({ defaultFlags, children }) => {
  const [isLoading, setIsLoading] = useState(true)
  const ldClient = useLDClient()
  // Have to put client in a Ref, so it's available in callback functions
  const ldClientRef = useRef(ldClient)
  useEffect(() => {
    if (ldClient) {
      // ldClient getting updated when context changed or flags updated. So, the moment when ldClient is not undefined and current ref is undefined - means the client got initialized
      if (ldClientRef.current === undefined) {
        log.info('LDClient initialized')
        setIsLoading(false)
      }
      ldClientRef.current = ldClient
    }
  }, [ldClient])

  const flags = useLDFlags()

  // Context to update once lDclient is initialized. Have to use Ref instead of state, because it will be used in callbacks
  const contextQueue = useRef<MultiContext>()

  // Process the context update when ldClient initializes
  useEffect(() => {
    if (ldClient && contextQueue.current) {
      const currentContext = ldClient.getContext() as MultiContext
      log.debug('Identifying context from a queue: ', contextQueue.current)
      void ldClient.identify({ ...currentContext, ...contextQueue.current })
      contextQueue.current = undefined
    }
  }, [ldClient])

  useEffect(() => {
    // Notificaitons listeners - on authenticated (either session loaded or signed in)
    const authenticatedListener = (data: unknown): void => {
      const eventData = data as { detail: { userId: string } }
      const userContext = {
        kind: 'user',
        key: eventData.detail.userId,
        customerId: eventData.detail.userId,
        anonymous: false,
      }
      log.debug('User is authenticated event. Event data: ', eventData.detail)
      if (ldClientRef.current) {
        const currentContext = ldClientRef.current.getContext() as MultiContext
        // Update LD context if customer id is available and was not set before
        if (currentContext.user.customerId !== eventData.detail.userId) {
          const contextToUpdate = produce(currentContext, (draft: MultiContext) => {
            draft.user = userContext
          })
          log.debug('Identifying updated context: ', contextToUpdate)
          // Waiting for the response is not needed, after the identify call is completed the flags will be updated
          void ldClientRef.current.identify(contextToUpdate)
        }
      } else {
        // ldClient was not intialized yet, queue the context update
        contextQueue.current = contextQueue.current
          ? produce(contextQueue.current, (draft: MultiContext) => {
              draft.user = userContext
            })
          : ({ user: userContext } as MultiContext)
        log.debug('ldClient is not initialized. Put context in a queue: ', { user: userContext })
      }
    }
    notifications.on(NOTIFICATIONS.AUTH.AUTHENTICATED, authenticatedListener)

    // Notificaitons listeners - on sign in with passkey (assumed that ldClient is already initialized)
    const customerDetailsListener = (data: unknown): void => {
      const eventData = data as { detail: { customer: { email: string } } }
      log.debug('Customer details event. Event data: ', eventData.detail)
      const currentContext = ldClientRef.current?.getContext() as MultiContext
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- currentContext can be undefined if ldClientRef is not initialized
      if (currentContext?.user.email !== eventData.detail.customer.email) {
        const contextToUpdate = produce(currentContext, (draft: MultiContext) => {
          draft.user.email = eventData.detail.customer.email
        })
        log.debug('Identifying updated context: ', contextToUpdate)
        void ldClientRef.current?.identify(contextToUpdate)
      }
    }
    notifications.on(NOTIFICATIONS.API.CUSTOMER_DETAILS, customerDetailsListener)

    // Notificaitons listeners - device service initialized
    const deviceDataListener = (data: unknown): void => {
      const eventData = data as { detail: DeviceData }
      const deviceContext = {
        kind: 'device',
        key: eventData.detail.visitorId,
        device: eventData.detail.device,
        browserName: eventData.detail.browserName,
        browserVersion: eventData.detail.browserVersion,
        incognito: eventData.detail.incognito,
        os: eventData.detail.os,
        osVersion: eventData.detail.osVersion,
        deviceSeenBeforeByFingerprint: eventData.detail.visitorFound,
        firstSeenAt: eventData.detail.firstSeenAt,
        lastSeenAt: eventData.detail.lastSeenAt,
      } as MultiContext['device']
      const ipLocationContext = {
        kind: 'ipLocation',
        key: `ip_address:${eventData.detail.ip}`,
        ipLocation: eventData.detail.ipLocation,
      } as MultiContext['ipLocation']

      log.debug('Device data event. Event data: ', { device: deviceContext, ipLocation: ipLocationContext })
      if (ldClientRef.current) {
        const currentContext = ldClientRef.current.getContext() as MultiContext
        const contextToUpdate = produce(currentContext, (draft: MultiContext) => {
          draft.device = deviceContext
          draft.ipLocation = ipLocationContext
        })
        log.debug('Identifying updated context: ', contextToUpdate)
        void ldClientRef.current.identify(contextToUpdate)
      } else {
        // ldClient was not intialized yet, queue the context update
        contextQueue.current = contextQueue.current
          ? produce(contextQueue.current, (draft: MultiContext) => {
              draft.device = deviceContext
              draft.ipLocation = ipLocationContext
            })
          : ({ device: deviceContext, ipLocation: ipLocationContext } as MultiContext)
        log.debug('ldClient is not initialized. Put context in a queue: ', {
          device: deviceContext,
          ipLocation: ipLocationContext,
        })
      }
    }
    notifications.on(NOTIFICATIONS.DEVICE.DATA, deviceDataListener)

    return () => {
      notifications.remove(NOTIFICATIONS.AUTH.SESSION_LOADED, authenticatedListener)
      notifications.remove(NOTIFICATIONS.API.CUSTOMER_DETAILS, customerDetailsListener)
      notifications.remove(NOTIFICATIONS.DEVICE.DATA, deviceDataListener)
    }
  }, [])

  // Track updated flags
  const updatedFlags = useMemo(() => ldClientRef.current?.allFlags(), [ldClientRef.current])

  // Dispatch notification when flags change
  useEffect(() => {
    log.debug('Dispatching "Feature Flags Updated" notification')
    notifications.dispatch(NOTIFICATIONS.LAUNCH_DARKLY.FLAGS_UPDATED, { flags: updatedFlags })
  }, [updatedFlags])

  return (
    <FeatureFlagsContext.Provider value={{ flags: { ...defaultFlags, ...flags }, isLoading }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export interface FeatureFlagsProviderProps extends PropsWithChildren {
  config: Config
  defaultFlags?: Flags
}

/**
 * Feature Provider context provider
 * @returns JSX.Element
 */
export const FeatureFlagsProvider: FC<FeatureFlagsProviderProps> = ({ config, defaultFlags, children }) => {
  if (!isValidConfig(config, configSchema)) {
    log.error('Configuration is invalid')
    throw new Error('Configuration is invalid')
  }

  const appPlatform = useMemo(() => platformService.getAppPlatform(), [])
  const appType = useMemo(() => platformService.getAppType(), [])
  const searchParams =
    // TODO: Remove validation after switched to React.js
    typeof window !== 'undefined' ? Object.fromEntries(new URLSearchParams(window.location.search)) : {}
  let userSourceKey = 'user_source_id'
  if (searchParams.utm_source) {
    userSourceKey = `${userSourceKey}:${searchParams.utm_source}`
  }
  if (searchParams.utm_medium) {
    userSourceKey = `${userSourceKey}:${searchParams.utm_medium}`
  }
  if (searchParams.utm_campaign) {
    userSourceKey = `${userSourceKey}:${searchParams.utm_campaign}`
  }
  if (searchParams.utm_term) {
    userSourceKey = `${userSourceKey}:${searchParams.utm_term}`
  }

  const defaultContext: MultiContext = {
    kind: 'multi',
    user: {
      kind: 'user',
      anonymous: true,
    },
    appConfig: {
      kind: 'appConfig',
      appName: config.appName,
      appVariant: config.appVariant,
      appVersion: config.appVersion,
      key: `app_config_id:${config.appName}:${config.appVariant}:${config.appVersion}`,
    },
    platform: {
      kind: 'platform',
      appName: config.appName,
      appPlatform,
      appType: appType?.toString() ?? '',
      appVersion: config.env,
      key: `platform_id:${config.appName}:${appPlatform}:${appType}:${config.env}`,
    },
    userSource: {
      kind: 'userSource',
      key: userSourceKey,
      referrer: typeof window !== 'undefined' ? window.document.referrer : '', // TODO: Remove validation after switched to React.js
      utm_campaign: searchParams.utm_campaign,
      utm_medium: searchParams.utm_medium,
      utm_source: searchParams.utm_source,
      utm_term: searchParams.utm_term,
    },
  }

  return (
    <LDProvider clientSideID={config.launchDarkly.clientSideId} context={defaultContext}>
      <IdentifyUserComponent defaultFlags={defaultFlags}>{children}</IdentifyUserComponent>
    </LDProvider>
  )
}

// Custom hook to access the Feature Flags
export const useFlags = <T,>(): { flags: T; isLoading: boolean } => {
  const { flags: featureFlags, isLoading } = useContext(FeatureFlagsContext)
  return { flags: featureFlags as T, isLoading }
}
